import dagre from "@dagrejs/dagre";
import type { Edge, Node } from "@xyflow/react";

const NODE_WIDTH = 260;
const NODE_HEIGHT = 56;

/** Per-edge layout hints consumed by dagre. */
export type EdgeLayoutData = { minlen?: number };

/**
 * Optional per-node height overrides keyed by node id. Lets callers tell
 * dagre that an interactive node (e.g. an expanded risk node) is taller
 * than the default so neighbors get pushed out of the way instead of
 * overlapping when content grows.
 */
export type NodeSizeOverrides = ReadonlyMap<string, { height: number }>;

export const layoutGraph = (
  nodes: Node[],
  edges: Edge[],
  sizeOverrides?: NodeSizeOverrides
): Node[] => {
  const g = new dagre.graphlib.Graph({ directed: true });
  g.setGraph({
    rankdir: "LR",
    nodesep: 18,
    ranksep: 40,
    marginx: 12,
    marginy: 12,
  });
  g.setDefaultEdgeLabel(() => ({}));
  for (const n of nodes) {
    const override = sizeOverrides?.get(n.id);
    g.setNode(n.id, {
      width: NODE_WIDTH,
      height: override?.height ?? NODE_HEIGHT,
    });
  }
  for (const e of edges) {
    const hint = (e.data as EdgeLayoutData | undefined) ?? {};
    g.setEdge(e.source, e.target, { minlen: hint.minlen ?? 1 });
  }
  dagre.layout(g);
  return nodes.map((n) => {
    const { x, y } = g.node(n.id);
    const override = sizeOverrides?.get(n.id);
    const height = override?.height ?? NODE_HEIGHT;
    return {
      ...n,
      position: { x: x - NODE_WIDTH / 2, y: y - height / 2 },
    };
  });
};
